/**
 * @module engine/state
 * @summary The single mutable state object shared across all engine modules.
 * @description
 *   The original tool kept everything in module-level `let` globals
 *   (shapes, zoom, panX, …). To preserve that exact, fast mutate-in-place
 *   model while still splitting the engine into files, we centralise all
 *   mutable state on ONE exported object `S`. Every engine module imports `S`
 *   and reads/writes `S.shapes`, `S.zoom`, etc. — the direct equivalent of the
 *   original globals. React NEVER imports `S`; it talks to the CanvasEngine API.
 *
 *   `// INVARIANT:` the RAF render loop is the only reader that runs every
 *   frame; mutators set `S.dirty = true` to request a repaint.
 *
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 954-1030 (the STATE,
 *   CANVAS and COORDS blocks).
 */
import type {
  Shape,
  Connection,
  DocMeta,
  Level,
  Pipe3,
  Dim3,
  Formulation,
  ScrewConfig,
  Tool,
  RouteMode,
  Guide,
  PortKey,
  EngineEvent,
} from '../types';

/** Grid spacing in world units (snap + dot grid). */
export const GRID = 20;

/** A single undo/redo snapshot (shallow-cloned arrays). */
export interface DocSnapshot {
  shapes: Shape[];
  connections: Connection[];
  /** 3D floors. Snapshotted so re-levelling a drawing is undoable. */
  levels: Level[];
  /**
   * Pipework that exists only in 3D. Part of the document (it saves and loads
   * with it) but never read by the 2D canvas.
   */
  pipes3: Pipe3[];
  /** Hand-placed 3D dimensions. Part of the document, invisible to the 2D canvas. */
  dims3: Dim3[];
  /** The recipes this drawing's lines run. Part of the document, read by both views. */
  formulations: Formulation[];
  /** The machines those lines run on. Part of the document; read by the simulator. */
  screwConfigs: ScrewConfig[];
}

/** Transient state while dragging a connector's segment. */
export interface ConnDragState {
  cn: Connection;
  segIdx: number;
  pts: { x: number; y: number }[];
  startX: number;
  startY: number;
  origMidX: number | undefined;
  origMidY: number | undefined;
}

/** Transient state while resizing a shape via a handle. */
export interface ResizeState {
  shape: Shape;
  handle: { id: string; x: number; y: number };
  sx: number;
  sy: number;
  orig: Shape;
}

/** A pending connection origin (connect tool). */
export interface ConnectFrom {
  shape: Shape;
  port: PortKey;
}

/** A library item being dragged onto the canvas. */
export interface DragLib {
  type: string;
  sx: number;
  sy: number;
}

/** The complete engine state. Mirrors the original's globals 1:1. */
export interface EngineState {
  // ── Document model ──
  shapes: Shape[];
  connections: Connection[];
  selected: string[];
  clipboard: Shape[];
  /** Connections between clipboard shapes (pasted with remapped ids). */
  clipboardConns: Connection[];
  docMeta: DocMeta;
  /**
   * 3D floors. Part of the document (they save and load with it) but read only
   * by the 3D view — the 2D canvas never looks at them.
   */
  levels: Level[];
  /**
   * Pipework that exists only in 3D. Part of the document (it saves and loads
   * with it) but never read by the 2D canvas.
   */
  pipes3: Pipe3[];
  /** Hand-placed 3D dimensions. Part of the document, invisible to the 2D canvas. */
  dims3: Dim3[];
  /** The recipes this drawing's lines run. Part of the document, read by both views. */
  formulations: Formulation[];
  /** The machines those lines run on. Part of the document; read by the simulator. */
  screwConfigs: ScrewConfig[];

  // ── History ──
  undoStack: DocSnapshot[];
  redoStack: DocSnapshot[];

  // ── View / tool ──
  tool: Tool;
  zoom: number;
  panX: number;
  panY: number;
  snapOn: boolean;
  gridOn: boolean;
  routeMode: RouteMode;
  /**
   * Millimetres per drawing unit, and the 3D view's vertical exaggeration.
   * These live here rather than in the 3D view's own options because they are
   * properties of the DRAWING: they are saved with it and apply to whoever
   * opens it, unlike the HUD toggles beside them in that panel.
   */
  scaleMm: number;
  heightScale: number;

  // ── Render bookkeeping ──
  dirty: boolean;
  hoveredId: string | null;
  shapeCounter: number;
  groupCounter: number;
  smartGuides: Guide[];

  // ── Canvas binding (set on mount) ──
  canvas: HTMLCanvasElement | null;
  ctx: CanvasRenderingContext2D | null;
  wrap: HTMLElement | null;
  CW: number;
  CH: number;
  dpr: number;
  /** Last pointer position in world coords (for the status bar readout). */
  coordsX: number;
  coordsY: number;

  // ── Transient interaction flags ──
  isDragging: boolean;
  dragOffsets: { id: string; dx: number; dy: number }[];
  isConnDragging: boolean;
  connDragState: ConnDragState | null;
  isPanning: boolean;
  panStart: { sx: number; sy: number; px: number; py: number } | null;
  isConnecting: boolean;
  connectFrom: ConnectFrom | null;
  connectPreview: { x: number; y: number } | null;
  isResizing: boolean;
  resizeState: ResizeState | null;
  lassoStart: { x: number; y: number } | null;
  lassoRect: { x: number; y: number; w: number; h: number } | null;
  dragLib: DragLib | null;
  justDroppedFromLib: boolean;
  spaceDown: boolean;
  mouseDown: boolean;
  mouseMoved: boolean;

  // ── Touch (pinch/pan) ──
  touchStart: { x: number; y: number; px: number; py: number } | null;
  pinchDist: number;
  pinchZoom: number;
}

/** Build a fresh, empty engine state. */
export function makeInitialState(): EngineState {
  return {
    shapes: [],
    connections: [],
    selected: [],
    clipboard: [],
    clipboardConns: [],
    docMeta: {
      docName: '',
      rev: 'A',
      drawnBy: '..............................',
      approvedBy: '..............................',
      date: new Date().toISOString().slice(0, 10),
    },
    levels: [],
    pipes3: [],
    dims3: [],
    formulations: [],
    screwConfigs: [],
    undoStack: [],
    redoStack: [],
    tool: 'select',
    zoom: 1,
    panX: 60,
    panY: 40,
    snapOn: true,
    gridOn: true,
    routeMode: 'manhattan',
    scaleMm: 25,
    heightScale: 1,
    dirty: true,
    hoveredId: null,
    shapeCounter: 0,
    groupCounter: 0,
    smartGuides: [],
    canvas: null,
    ctx: null,
    wrap: null,
    CW: 0,
    CH: 0,
    dpr: 1,
    coordsX: 0,
    coordsY: 0,
    isDragging: false,
    dragOffsets: [],
    isConnDragging: false,
    connDragState: null,
    isPanning: false,
    panStart: null,
    isConnecting: false,
    connectFrom: null,
    connectPreview: null,
    isResizing: false,
    resizeState: null,
    lassoStart: null,
    lassoRect: null,
    dragLib: null,
    justDroppedFromLib: false,
    spaceDown: false,
    mouseDown: false,
    mouseMoved: false,
    touchStart: null,
    pinchDist: 0,
    pinchZoom: 1,
  };
}

/** The shared, mutable engine state singleton. */
export const S: EngineState = makeInitialState();

/** Request a repaint on the next animation frame. */
export function markDirty(): void {
  S.dirty = true;
}

// ─── Lightweight event bus (engine → React bridge) ───
// React subscribes to coarse events; the engine emits them at meaningful
// moments (selection changed, history changed, …) — never per mouse-move.

type Listener = () => void;
const listeners = new Map<EngineEvent, Set<Listener>>();

/** Subscribe to an engine event. Returns an unsubscribe function. */
export function on(evt: EngineEvent, cb: Listener): () => void {
  let set = listeners.get(evt);
  if (!set) {
    set = new Set();
    listeners.set(evt, set);
  }
  set.add(cb);
  return () => set!.delete(cb);
}

/** Emit an engine event to all subscribers. */
export function emit(evt: EngineEvent): void {
  listeners.get(evt)?.forEach((cb) => cb());
}
